/**
 * Turning a `lastSeenAt` stamp into the line under a correspondent's name.
 *
 * The API refreshes the stamp at most once a minute, so anything finer than
 * "moments ago" would be reporting precision the data does not have. The
 * buckets widen from there because the answer people actually want is "can I
 * expect a reply", not a duration.
 */
export type PresenceBucket =
  | { kind: 'never' }
  | { kind: 'online' }
  | { kind: 'minutes'; minutes: number }
  | { kind: 'hours'; hours: number }
  | { kind: 'days'; days: number };

/** Within this, treat them as here now — it is inside the stamp's own accuracy. */
const ONLINE_MS = 2 * 60_000;

export function presenceBucket(lastSeenAt: string | null, now = Date.now()): PresenceBucket {
  // Never seen is a real state, not "a very long time ago": the column is
  // nullable with no backfill, so every account predating it starts here.
  if (!lastSeenAt) return { kind: 'never' };

  const elapsed = now - new Date(lastSeenAt).getTime();
  // A clock skewed ahead of the server should read as present, not as a
  // negative duration.
  if (elapsed < ONLINE_MS) return { kind: 'online' };

  const minutes = Math.floor(elapsed / 60_000);
  if (minutes < 60) return { kind: 'minutes', minutes };

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return { kind: 'hours', hours };

  return { kind: 'days', days: Math.floor(hours / 24) };
}
